import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  CheckCircle,
  QrCode2,
  Logout as LogoutIcon,
  Refresh,
} from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import {
  getCurrentMinuteSlot,
  checkInBallkid,
  checkOutBallkid,
  getCheckInStatusDetailed,
  getAvailableBallkidsForDay,
  invalidateCheckInCache,
  getBallkids,
  getBallkidNameById,
} from "../firebaseService";
import { useGroup } from "../context/GroupContext";
import { useTournamentConfig } from "../context/TournamentConfigContext";

const formatTime = (isoString) => {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const CheckInTestPage = () => {
  const { activeGroup } = useGroup();
  const { daysOfWeek } = useTournamentConfig();
  const memberLabel = activeGroup?.memberLabel || "Ballkinder";

  const [selectedDay, setSelectedDay] = useState(daysOfWeek?.[0] || "");
  const [allBallkids, setAllBallkids] = useState([]);
  const [selectedBallkid, setSelectedBallkid] = useState("");
  const [availableBallkids, setAvailableBallkids] = useState([]);
  const [checkInStatus, setCheckInStatus] = useState({});
  const [minuteSlot, setMinuteSlot] = useState(getCurrentMinuteSlot());
  const [lastAction, setLastAction] = useState(null);

  // Load all ballkids
  useEffect(() => {
    getBallkids().then(setAllBallkids);
  }, []);

  // Set first ballkid as default
  useEffect(() => {
    if (allBallkids.length > 0 && !selectedBallkid) {
      setSelectedBallkid(allBallkids[0].id);
    }
  }, [allBallkids, selectedBallkid]);

  // Rotate QR every minute
  useEffect(() => {
    const interval = setInterval(() => setMinuteSlot(getCurrentMinuteSlot()), 5000);
    return () => clearInterval(interval);
  }, []);

  // Load status
  const loadStatus = useCallback(async () => {
    if (!selectedDay) return;
    const [available, status] = await Promise.all([
      getAvailableBallkidsForDay(selectedDay),
      getCheckInStatusDetailed(selectedDay),
    ]);
    setAvailableBallkids(available);
    setCheckInStatus(status);
  }, [selectedDay]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // QR code value
  const qrValue = useMemo(() => {
    if (!selectedBallkid) return null;
    const payload = JSON.stringify({ id: selectedBallkid, ts: minuteSlot });
    return "BK:" + btoa(payload);
  }, [selectedBallkid, minuteSlot]);

  // Simulate scan (what the scanner page would do)
  const simulateScan = async () => {
    if (!qrValue || !selectedDay) return;

    // Decode same as scanner would
    const jsonStr = atob(qrValue.slice(3));
    const { id, ts } = JSON.parse(jsonStr);

    // Check timestamp
    const currentSlot = getCurrentMinuteSlot();
    if (Math.abs(currentSlot - ts) > 2) {
      setLastAction({ name: "?", action: "error", msg: "QR-Code abgelaufen!" });
      return;
    }

    const name = await getBallkidNameById(id);
    const status = checkInStatus[id];

    if (status?.checkedIn && !status?.checkedOut) {
      await checkOutBallkid(id, selectedDay);
      setLastAction({ name, action: "out" });
    } else {
      await checkInBallkid(id, selectedDay);
      setLastAction({ name, action: "in" });
    }

    invalidateCheckInCache();
    await loadStatus();
    setTimeout(() => setLastAction(null), 3000);
  };

  // Direct check-in (manual)
  const manualCheckIn = async (ballkidId) => {
    const status = checkInStatus[ballkidId];
    const name = await getBallkidNameById(ballkidId);
    if (status?.checkedIn && !status?.checkedOut) {
      await checkOutBallkid(ballkidId, selectedDay);
      setLastAction({ name, action: "out" });
    } else {
      await checkInBallkid(ballkidId, selectedDay);
      setLastAction({ name, action: "in" });
    }
    invalidateCheckInCache();
    await loadStatus();
    setTimeout(() => setLastAction(null), 3000);
  };

  const checkedInCount = Object.values(checkInStatus).filter((s) => s.checkedIn && !s.checkedOut).length;
  const selectedBallkidName = allBallkids.find((b) => b.id === selectedBallkid)?.name || "";

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Check-In Test-Seite
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Hier kannst du den kompletten Check-In Flow testen: QR-Code generieren, Scan simulieren, und den Status live sehen.
        Alles schreibt in die echte <code>check_in_out</code> Collection — ist also auch fuer die Abrechnung sichtbar.
      </Alert>

      {/* Day Selection */}
      <FormControl sx={{ mb: 3, minWidth: 200 }}>
        <InputLabel>Turniertag</InputLabel>
        <Select value={selectedDay} label="Turniertag" onChange={(e) => setSelectedDay(e.target.value)}>
          {daysOfWeek.map((day) => (
            <MenuItem key={day} value={day}>{day}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Grid container spacing={3}>
        {/* LEFT: Ballkind Simulation */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, bgcolor: "#191f34" }}>
            <Typography variant="h6" sx={{ mb: 2, color: "white", textAlign: "center" }}>
              Ballkind-Seite (Simulation)
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: "rgba(255,255,255,0.7)" }}>Ballkind waehlen</InputLabel>
              <Select
                value={selectedBallkid}
                label="Ballkind waehlen"
                onChange={(e) => setSelectedBallkid(e.target.value)}
                sx={{ color: "white", ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" } }}
              >
                {allBallkids.map((bk) => (
                  <MenuItem key={bk.id} value={bk.id}>{bk.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedBallkidName && (
              <Typography variant="h5" sx={{ color: "white", textAlign: "center", mb: 2 }}>
                Hallo {selectedBallkidName.split(" ")[0]}
              </Typography>
            )}

            {/* QR Code */}
            {qrValue && (
              <Box sx={{ textAlign: "center" }}>
                <Paper sx={{ p: 3, display: "inline-block", bgcolor: "white", borderRadius: 3 }}>
                  <QRCodeSVG value={qrValue} size={180} level="M" />
                  <Typography variant="body2" sx={{ mt: 1, color: "#333", fontWeight: 600 }}>
                    Zeige diesen Code am Eingang
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#999", display: "block" }}>
                    Slot: {minuteSlot} (rotiert jede Minute)
                  </Typography>
                </Paper>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* RIGHT: Scanner Simulation */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Scanner (Simulation)
            </Typography>

            <Button
              variant="contained"
              color="success"
              size="large"
              fullWidth
              onClick={simulateScan}
              sx={{ mb: 2, py: 1.5, fontWeight: 700, fontSize: "1rem" }}
            >
              <QrCode2 sx={{ mr: 1 }} />
              Scan simulieren → {selectedBallkidName || "..."}
            </Button>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Klick = simuliert das Scannen des QR-Codes links. Erneut klicken = Check-Out.
            </Typography>

            {/* Action Result */}
            {lastAction && (
              <Alert
                severity={lastAction.action === "in" ? "success" : lastAction.action === "out" ? "warning" : "error"}
                icon={<CheckCircle fontSize="large" />}
                sx={{ mb: 2, fontSize: "1.1rem", fontWeight: 700 }}
              >
                {lastAction.action === "error"
                  ? lastAction.msg
                  : `${lastAction.name} ${lastAction.action === "in" ? "eingecheckt!" : "ausgecheckt!"}`
                }
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Stats */}
            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Chip icon={<CheckCircle />} label={`${checkedInCount} eingecheckt`} color="success" sx={{ fontWeight: 600 }} />
              <Chip label={`${availableBallkids.length} angemeldet`} color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
              <Button size="small" startIcon={<Refresh />} onClick={loadStatus}>Refresh</Button>
            </Box>

            {/* Status Table */}
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>In</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Out</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(availableBallkids.length > 0 ? availableBallkids : allBallkids.slice(0, 10)).map((bk) => {
                    const status = checkInStatus[bk.id];
                    const isIn = status?.checkedIn && !status?.checkedOut;
                    const isOut = status?.checkedIn && status?.checkedOut;
                    return (
                      <TableRow key={bk.id} sx={{ bgcolor: isIn ? "rgba(76,175,80,0.08)" : "transparent" }}>
                        <TableCell>{bk.name}</TableCell>
                        <TableCell align="center">
                          {isOut ? (
                            <Chip label="Fertig" size="small" variant="outlined" />
                          ) : isIn ? (
                            <Chip label="Anwesend" size="small" color="success" />
                          ) : (
                            <Chip label="—" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.8rem" }}>{formatTime(status?.checkIn)}</TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.8rem" }}>{formatTime(status?.checkOut)}</TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            color={isIn ? "warning" : "success"}
                            onClick={() => manualCheckIn(bk.id)}
                            sx={{ fontSize: "0.7rem", minWidth: 0, px: 1 }}
                          >
                            {isIn ? <LogoutIcon fontSize="small" /> : "In"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CheckInTestPage;
